import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import IconButton, { IconButtonStyledSecondary } from '../Button/Iconbutton';
import { SearchIconWrapper, StyledIcon } from './styling';
import { Size } from '../types';
import { COLORS, LoadingIndicator } from '..';
import { SystemIcons } from '../icons';
import SearchBarInput from './components/SearchBarInput';
import SearchField, { StyledSearchField } from './components/SearchField';

const Wrapper = styled.div<{$shouldNotInteract: boolean}>`
  width: 100%;

  ${StyledSearchField} {
    transition: box-shadow 0.2s ease-in-out, width 0.2s ease-in-out;

    background-color: transparent;

    &.small {
      width: 48px;
    }

    &.medium {
      width: 56px;
    }

    &.expanded {
      width: 100%;
    }

    box-shadow: none;

    &:hover {
      box-shadow: none;
    }

    &.expanded {
      background-color: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'default'}, props.theme)};
      box-shadow: inset 0 0 0 1px ${props => COLORS.generateToken({componentType:'border', defaultVariant:'default'}, props.theme)};
    }

    &.expanded:hover,
    &.expanded:focus-within {
      box-shadow: inset 0 0 0 2px ${props => COLORS.generateToken({componentType:'border', state:'hover'}, props.theme)};
    }
  }

  ${IconButtonStyledSecondary} {
    pointer-events: ${(props) => (props.$shouldNotInteract ? 'none' : 'auto')};
  }
`;

export type QuickSearchProps = {
  /** Required. Id of the component. */
  id: string;
  /** Optional. Current search term entered by the user. */
  searchTerm?: string;
  /** Required. Handler to be called when user modified input. */
  setSearchTerm: (term: string) => void;
  /** Required. Handler to be called when user triggers search. */
  enterSearch: (e: any) => void;
  /** Required. Handler to be called when user clears input field. */
  removeSearch: (e: any) => void;
  /** Optional. Placeholder text to be shown in the input field when there is no input. */
  placeholder?: string;
  /** Optional. ARIA label to set on the input field. */
  performSearchLabel?: string;
  /** Optional. If disabled user can not interact with the search bar. */
  disabled?: boolean;
  /** Optional. Optional onkeydown handler to be set on the input field. */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Optional. Size of the component. Defaults to 'medium'.   */
  size?: Size.Small | Size.Medium;
  /** Optional. Margin style attribute to be set on the input field. */
  margin?: string;
  /** Optional. If set then loading indicator will be shown on the right side of the input field. */
  loading?: boolean;
};

const QuickSearch: React.FunctionComponent<QuickSearchProps> = ({
  id,
  searchTerm,
  setSearchTerm,
  enterSearch,
  removeSearch,
  placeholder,
  performSearchLabel,
  disabled,
  onKeyDown,
  size,
  margin,
  loading,
}: QuickSearchProps) => {
  const theme = useTheme();
  const searchFieldRef = React.useRef<HTMLDivElement>(null);
  const searchFieldInputRef = React.useRef<HTMLInputElement>(null);

  const [searchFieldVisible, setSearchFieldVisible] = React.useState<{ visible: boolean; focus?: boolean }>({
    visible: false,
  });

  const handleClickOutside = (e: any) => {
    if (searchFieldRef?.current && !searchFieldRef.current.contains(e.target)) {
      if (searchFieldVisible?.visible && !searchTerm?.length) {
        setSearchFieldVisible({ visible: false });
      }
    }
  };

  const handleKeyDown = (e: any) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
    if (e.key === 'Escape') {
      if (searchFieldVisible?.visible && !searchTerm?.length) {
        setSearchFieldVisible({ visible: false });
      }
    }
  };

  const handleBlur = (e: any) => {
    if (!searchTerm && (!e.relatedTarget || !searchFieldRef.current?.contains(e.relatedTarget))) {
      setSearchFieldVisible({ visible: false });
    }
  };

  const handleSearchIconAction = (e: any) => {
    if (!searchFieldVisible?.visible) {
      setSearchFieldVisible({
        visible: !searchFieldVisible.visible,
        focus: !searchFieldVisible.visible && e?.detail !== 1,
      });
    }
  };

  // set search field visibility on search term change
  React.useEffect(() => {
    if (!!searchTerm?.length && !searchFieldVisible?.visible) {
      setSearchFieldVisible({ visible: true, focus: false });
    }
  }, [searchTerm, searchFieldVisible]);

  // register click outside handler
  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // focus search input on visibility change
  React.useEffect(() => {
    if (searchFieldVisible?.visible) {
      searchFieldInputRef?.current?.focus({ focusVisible: searchFieldVisible.focus } as any);
    }
  }, [searchFieldVisible.visible, searchFieldInputRef]);

  return (
    <Wrapper $shouldNotInteract={searchFieldVisible?.visible}>
      <SearchField
        ref={searchFieldRef}
        id={id}
        size={size}
        searchTerm={searchTerm}
        style={{
          margin: margin,
        }}
        onBlur={handleBlur}
        className={searchFieldVisible?.visible ? 'expanded' : ''}>
        {searchFieldVisible?.visible && (
          <SearchBarInput
            placeholder={disabled ? '' : placeholder}
            id={id}
            ref={searchFieldInputRef}
            disabled={disabled}
            onKeyDown={handleKeyDown}
            size={size}
            setSearchTerm={(term: string) => setSearchTerm(term)}
            searchTerm={searchTerm}
            enterSearch={enterSearch}
            focusParentRefs={[searchFieldRef]}
          />
        )}
        <SearchIconWrapper className={size ? size : ''} role="button" aria-label={performSearchLabel}>
          <IconButton
            id={`${id}_Searchbutton`}
            dataTestId={`${id}_Searchbutton`}
            variant="secondary"
            shape="circular"
            tabIndex={searchFieldVisible?.visible ? -1 : 0}
            action={handleSearchIconAction}>
            <SystemIcons.Search size="24px" color={disabled ? COLORS.generateToken({componentType:'icon', state:'disabled'}, theme) : COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, theme)} />
          </IconButton>
        </SearchIconWrapper>
        <StyledIcon className={size ? size : ''} role="button" hidden={!searchTerm || !searchFieldVisible?.visible}>
          <IconButton
            id={`${id}_Clearicon`}
            dataTestId={`${id}_Clearicon`}
            variant="secondary"
            shape="circular"
            action={(e) => {
              removeSearch(e);
              searchFieldInputRef?.current?.focus();
            }}>
            <SystemIcons.Clear size="24px" color={disabled ? COLORS.generateToken({componentType:'icon', state:'disabled'}, theme) : COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, theme)} />
          </IconButton>
        </StyledIcon>
        {loading && searchFieldVisible?.visible && (
          <StyledIcon className={size ? size : ''}>
            <LoadingIndicator size={Size.Small} color={COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, theme)} />
          </StyledIcon>
        )}
      </SearchField>
    </Wrapper>
  );
};

export default QuickSearch;
